import { generateSlug } from '@/lib/utils';

export type ContentTagRef = {
  name: string;
  slug: string;
  /** Harici URL (tag_url). Doluysa içerik linki buraya gider. */
  url?: string | null;
  /** Bilgi paneli / hover için görsel veya açıklama varsa true */
  hasInfo?: boolean;
};

const SKIP_TAGS = new Set([
  'a',
  'script',
  'style',
  'code',
  'pre',
  'iframe',
  'svg',
  'button',
  'textarea',
  'noscript',
]);

/** URL / domain parçası — bu karakterlerden sonra etiket eşleşmesin */
const URLISH_BEFORE = new Set(['/', '.', '@', ':', '=', '?', '&', '#']);

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function normalizeTagKey(value: string): string {
  return value.trim().toLocaleLowerCase('tr-TR');
}

export function resolveTagHref(slug: string, tagUrl?: string | null): string {
  const raw = (tagUrl || '').trim();
  if (!raw) return `/haberleri/${encodeURIComponent(slug)}`;
  if (/^https?:\/\//i.test(raw)) return raw;
  if (raw.startsWith('/') && !raw.startsWith('//')) return raw;
  // Reject protocol-relative and bare hosts without scheme
  if (raw.startsWith('//') || /^[a-z][a-z0-9+.-]*:/i.test(raw)) {
    return `/haberleri/${encodeURIComponent(slug)}`;
  }
  return `https://${raw}`;
}

/** HTML’de bu etiketin hedef href’i zaten varsa “harcanmış” sayılır */
function seedLinkedCounts(
  html: string,
  tags: Array<{ key: string; href: string; slug: string }>,
): Map<string, number> {
  const counts = new Map<string, number>();
  for (const tag of tags) {
    const hrefEscaped = escapeRegExp(tag.href);
    const hrefRe = new RegExp(
      `<a\\b[^>]*\\bhref=["']${hrefEscaped}(?:["'/#?]|$)`,
      'i',
    );
    if (hrefRe.test(html)) {
      counts.set(tag.key, 1);
      continue;
    }
    const slugRe = escapeRegExp(tag.slug);
    const internalRe = new RegExp(
      `<a\\b[^>]*\\bhref=["'][^"']*/haberleri/${slugRe}(?:["'/#?]|$)`,
      'i',
    );
    if (internalRe.test(html)) {
      counts.set(tag.key, 1);
    }
  }
  return counts;
}

/**
 * Yalnızca düz metin parçalarına uygula; etiket/attribute içine dokunma.
 * Bir etiket eklendikten sonra tekrar tokenize edilir — `/haberleri/...` bozulmaz.
 */
function mapTextChunks(html: string, mapFn: (text: string) => string): string {
  if (!html) return html;
  let out = '';
  const tokenRe = /(<\/?[a-zA-Z][^>]*>)|([^<]+)/g;
  for (const match of html.matchAll(tokenRe)) {
    if (match[1]) out += match[1];
    else if (match[2]) out += mapFn(match[2]);
  }
  return out || html;
}

type PreparedTag = {
  key: string;
  name: string;
  slug: string;
  href: string;
  hasInfo: boolean;
  /** RegExp source (global state yok — her kullanımda yeni RegExp) */
  patternSource: string;
};

function linkOnceInPlainText(
  text: string,
  tag: PreparedTag,
  linkedCount: Map<string, number>,
  maxPerTag: number,
  className: string,
): string {
  const count = linkedCount.get(tag.key) || 0;
  if (count >= maxPerTag || !text) return text;

  const pattern = new RegExp(tag.patternSource, 'giu');

  let replaced = false;
  return text.replace(
    pattern,
    (full, before: string, word: string, offset: number, input: string) => {
      if (replaced) return full;
      if ((linkedCount.get(tag.key) || 0) >= maxPerTag) return full;
      // /haberleri, www.haber.com, mailto: vb. içinde eşleşme
      if (before && URLISH_BEFORE.has(before)) return full;
      // haber.com — kök + TLD (lookahead eşleşmeye dahil değil)
      const after = input.slice(offset + full.length);
      if (/^\.[a-z]{2,}\b/i.test(after)) return full;
      replaced = true;
      linkedCount.set(tag.key, (linkedCount.get(tag.key) || 0) + 1);
      const safeHref = tag.href.replace(/"/g, '&quot;');
      const rel = tag.href.startsWith('http')
        ? ' rel="noopener noreferrer" target="_blank"'
        : '';
      const infoClass = tag.hasInfo ? ' news-inline-tag-has-info' : '';
      const infoData = tag.hasInfo ? ' data-tag-has-info="1"' : '';
      return `${before}<a href="${safeHref}" class="${className}${infoClass}" data-tag-slug="${tag.slug.replace(/"/g, '')}"${infoData}${rel}>${word}</a>`;
    },
  );
}

/**
 * Haber gövdesinde geçen sistem etiketlerini linkler.
 * - Harici tagUrl varsa oraya, yoksa `/haberleri/{slug}`
 * - Zaten <a> / script / code içindeyse dokunulmaz
 * - Attribute ve URL parçalarına dokunulmaz
 * - Her etiket en fazla 1 kez linklenir
 * - Yalnızca tam kelime: HABER≠HABERLER, APPLE≠MAPPLE
 */
export function linkArticleTagsInHtml(
  html: string,
  tags: ContentTagRef[],
  options?: { maxPerTag?: number; className?: string },
): string {
  const maxPerTag = Math.max(1, options?.maxPerTag ?? 1);
  const className = options?.className ?? 'news-inline-tag';

  const byKey = new Map<string, PreparedTag>();
  for (const t of tags) {
    const name = (t.name || '').trim();
    const slug = (t.slug || generateSlug(name)).trim();
    if (name.length < 2 || !slug) continue;
    const key = normalizeTagKey(name);
    if (!byKey.has(key)) {
      const href = resolveTagHref(slug, t.url);
      const escaped = escapeRegExp(name);
      // Tam kelime sınırı: öncesi/sonrası harf-rakam olamaz
      // HABER ↛ HABERLER, APPLE ↛ MAPPLE
      byKey.set(key, {
        key,
        name,
        slug,
        href,
        hasInfo: Boolean(t.hasInfo),
        patternSource: `(^|[^\\p{L}\\p{N}_])(${escaped})(?=[^\\p{L}\\p{N}_]|$)`,
      });
    }
  }

  const cleaned = Array.from(byKey.values()).sort(
    (a, b) => b.name.length - a.name.length,
  );

  if (!html?.trim() || cleaned.length === 0) return html;

  const linkedCount = seedLinkedCounts(html, cleaned);
  const allLinked = () =>
    cleaned.every((t) => (linkedCount.get(t.key) || 0) >= maxPerTag);

  let depthSkip = 0;
  let out = '';
  const tokenRe = /(<\/?[a-zA-Z][^>]*>)|([^<]+)/g;

  for (const match of html.matchAll(tokenRe)) {
    const tagHtml = match[1];
    const text = match[2];

    if (tagHtml) {
      const nameMatch = /^<\/?\s*([a-zA-Z0-9:-]+)/.exec(tagHtml);
      const name = (nameMatch?.[1] || '').toLowerCase();
      const isClose = tagHtml.startsWith('</');
      const isSelf =
        /\/>$/.test(tagHtml) ||
        /^(br|img|hr|input|meta|link|source|wbr)$/i.test(name);

      if (SKIP_TAGS.has(name) && !isSelf) {
        if (isClose) depthSkip = Math.max(0, depthSkip - 1);
        else depthSkip += 1;
      }
      out += tagHtml;
      continue;
    }

    if (!text || depthSkip > 0 || allLinked()) {
      out += text || '';
      continue;
    }

    // İlk eşleşmeye kadar düz metin; HTML eklendikten sonra tokenize et
    let segment = text;
    let hasInsertedHtml = false;
    for (const tag of cleaned) {
      if ((linkedCount.get(tag.key) || 0) >= maxPerTag) continue;
      if (hasInsertedHtml) {
        segment = mapTextChunks(segment, (chunk) =>
          linkOnceInPlainText(chunk, tag, linkedCount, maxPerTag, className),
        );
      } else {
        const next = linkOnceInPlainText(
          segment,
          tag,
          linkedCount,
          maxPerTag,
          className,
        );
        if (next !== segment) {
          hasInsertedHtml = true;
          segment = next;
        }
      }
    }
    out += segment;
  }

  return out || html;
}

/** AI / serbest metinden gelen etiket adlarını yalnızca sistemde var olanlara indirger */
export function filterToExistingTagNames(
  candidates: string[],
  existingTagNames: string[],
): string[] {
  if (!candidates.length || !existingTagNames.length) return [];
  const byKey = new Map(
    existingTagNames
      .map((name) => name.trim())
      .filter(Boolean)
      .map((name) => [normalizeTagKey(name), name] as const),
  );
  const out: string[] = [];
  const seen = new Set<string>();
  // En uzun aday önce — kısa etiket yanlış seçilmesin
  const ordered = [...candidates].sort(
    (a, b) => String(b).trim().length - String(a).trim().length,
  );
  for (const raw of ordered) {
    const key = normalizeTagKey(raw);
    const canonical = byKey.get(key);
    if (!canonical || seen.has(key)) continue;
    seen.add(key);
    out.push(canonical);
  }
  return out;
}

export function tagsFromSelectedNames(names: string[]): ContentTagRef[] {
  return names
    .map((name) => name.trim())
    .filter(Boolean)
    .map((name) => ({ name, slug: generateSlug(name) }));
}
